import { formatErrorMessages } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain/errors';
import { Text } from '@soteria/design-system-ve';

import * as styles from './ErrorBanner.css';

export interface ErrorBannerProps {
  /**
   * Errors to display - can be a string, array of domain errors, or null
   */
  errors?: DomainError[] | string | null;

  /**
   * Optional title to display above the errors
   * @default "An error occurred:"
   */
  title?: string;

  /**
   * Whether to show the correlation ID if available
   * @default true
   */
  showCorrelationId?: boolean;

  /**
   * Whether to style the banner as text only (no background or border)
   * @default false
   */
  textOnly?: boolean;
}

/**
 * A reusable error banner component that displays a list of errors
 * with optional correlation ID
 */
// eslint-disable-next-line complexity
export function ErrorBanner({ errors, title = 'An error occurred:', showCorrelationId = true, textOnly }: ErrorBannerProps) {
  // Return null if no errors
  if (!errors) {
    return null;
  }

  const bannerStyle = textOnly ? styles.textOnlyErrorBanner : styles.errorBanner;

  // Handle simple string error
  if (typeof errors === 'string') {
    return (
      <div className={bannerStyle}>
        {title && <Text className={styles.errorTitle}>{title}</Text>}
        <Text className={styles.errorText}>{errors}</Text>
      </div>
    );
  }

  // Handle domain errors array
  if (Array.isArray(errors)) {
    if (errors.length === 0) {
      return null;
    }

    const errorMessages = formatErrorMessages(errors);
    const correlationId = errors[0]?.correlationId;
    const hasCorrelationId = showCorrelationId && correlationId;

    return (
      <div className={bannerStyle}>
        {title && <Text className={styles.errorTitle}>{title}</Text>}

        {errorMessages.length === 1 ? (
          // Single error - display as simple text without bullets
          <Text className={styles.errorText}>{errorMessages[0]}</Text>
        ) : (
          // Multiple errors - display as bulleted list
          <ul className={styles.errorList}>
            {errorMessages.map(message => (
              <li key={`error-${message}`}>
                <Text className={styles.errorText}>{message}</Text>
              </li>
            ))}
          </ul>
        )}

        {hasCorrelationId && <Text className={`${styles.errorText} ${styles.correlationId}`}>Correlation ID: {correlationId}</Text>}
      </div>
    );
  }

  return null;
}
